// VoiceService.js
class VoiceService {
  constructor() {
    this.synth = window.speechSynthesis;
    this.isMuted = false;
    this.currentUtterance = null;
    this.voices = [];
    this.preferredVoice = null;
    this.isSpeaking = false;
    
    // Load voices
    this.loadVoices();
    
    // Chrome loads voices asynchronously
    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = () => this.loadVoices();
    }
  }

  loadVoices() {
    this.voices = this.synth.getVoices();
    this.preferredVoice = this.selectBestVoice();
  }

  selectBestVoice() {
    if (this.voices.length === 0) return null;

    // Priority list of high-quality voices
    const preferredVoiceNames = [
      'Google US English',
      'Google UK English Female',
      'Google UK English Male',
      'Samantha',
      'Karen',
      'Microsoft Zira Desktop',
      'Microsoft Aria Online (Natural)',
      'Microsoft Jenny Online (Natural)',
    ];

    // Try to find a preferred voice
    for (const name of preferredVoiceNames) {
      const voice = this.voices.find(v => v.name.includes(name));
      if (voice) return voice;
    }

    // Fallback: Find any natural-sounding English voice
    const naturalVoice = this.voices.find(v => 
      v.lang.startsWith('en') && 
      (v.name.includes('Natural') || v.name.includes('Premium'))
    );

    if (naturalVoice) return naturalVoice;

    // Last resort: Use first English voice
    return this.voices.find(v => v.lang.startsWith('en')) || this.voices[0];
  }

  speak(text, options = {}) {
    return new Promise((resolve, reject) => {
      if (this.isMuted || !text) {
        resolve();
        return;
      }

      // Reload voices if empty (sometimes needed)
      if (this.voices.length === 0) {
        this.loadVoices();
      }

      // Wait for previous speech to complete
      if (this.isSpeaking) {
        this.synth.cancel();
      }

      const utterance = new SpeechSynthesisUtterance(text);
      
      // Set the best available voice
      if (this.preferredVoice) {
        utterance.voice = this.preferredVoice;
      }
      
      utterance.rate = options.rate || 0.95;
      utterance.pitch = options.pitch || 1;
      utterance.volume = options.volume || 1;
      utterance.lang = options.lang || 'en-US';

      this.currentUtterance = utterance;
      this.isSpeaking = true;

      utterance.onend = () => {
        this.currentUtterance = null;
        this.isSpeaking = false;
        resolve();
      };

      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        this.currentUtterance = null;
        this.isSpeaking = false;
        
        // Don't reject on error, just resolve to continue
        // Some browsers throw errors even when speech works
        if (event.error !== 'canceled') {
          console.warn('Speech error but continuing:', event.error);
        }
        resolve();
      };

      // Small delay to ensure previous speech is fully stopped
      setTimeout(() => {
        try {
          this.synth.speak(utterance);
          
          // Workaround for Chrome bug where speech stops
          // Resume if paused (Chrome sometimes pauses inexplicably)
          setTimeout(() => {
            if (this.synth.paused) {
              this.synth.resume();
            }
          }, 100);
        } catch (error) {
          console.error('Error speaking:', error);
          this.isSpeaking = false;
          resolve();
        }
      }, 50);
    });
  }

  stop() {
    if (this.synth.speaking || this.synth.pending) {
      this.synth.cancel();
    }
    this.currentUtterance = null;
    this.isSpeaking = false;
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    if (this.isMuted) {
      this.stop();
    }
    return this.isMuted;
  }

  setMute(value) {
    this.isMuted = value;
    if (this.isMuted) {
      this.stop();
    }
  }

  checkAudioSupport() {
    return 'speechSynthesis' in window;
  }
}

export const voiceService = new VoiceService();